import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Pencil, Check, X, CreditCard, ChevronRight, Rocket } from "lucide-react";
import { toast } from "sonner";
import { PageContainer, PageHeader, Card, Input } from "../components/kit";

function InfoRow({ label, value, mono = false }) {
  return (
    <div className="py-5 border-b border-border last:border-0 flex items-center justify-between gap-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground w-36 flex-shrink-0">{label}</div>
      <div className={`flex-1 text-sm text-[#0F172A] ${mono ? "font-mono" : "font-medium"}`}>
        {value || <span className="italic text-muted-foreground">Not set</span>}
      </div>
    </div>
  );
}

function EditableRow({ label, value, placeholder, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft.trim() || null);
      setEditing(false);
    } catch {
      toast.error("Failed to save. Try again.");
    } finally { setSaving(false); }
  };
  const cancel = () => { setDraft(value || ""); setEditing(false); };

  return (
    <div className="py-5 border-b border-border last:border-0 flex items-center justify-between gap-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground w-36 flex-shrink-0">{label}</div>
      {editing ? (
        <div className="flex-1 flex items-center gap-2 flex-wrap">
          <Input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
            placeholder={placeholder} className="h-10 max-w-xs py-2" />
          <button onClick={save} disabled={saving} className="text-[#0025D8] hover:opacity-70 disabled:opacity-40">
            <Check className="w-4 h-4" />
          </button>
          <button onClick={cancel} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-between group">
          <span className="text-sm font-medium text-[#0F172A]">
            {value || <span className="italic text-muted-foreground font-normal">Not set</span>}
          </span>
          <button onClick={() => { setDraft(value || ""); setEditing(true); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-[#0025D8] ml-2"
            title={`Edit ${label.toLowerCase()}`}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();

  const handleSaveName = async (name) => {
    if (!name) { toast.error("Name cannot be empty."); throw new Error(); }
    const { data } = await api.patch("/auth/me", { name });
    setUser(data);
    toast.success("Name updated");
  };

  const handleSaveTargetRole = async (target_job_title) => {
    const { data } = await api.patch("/auth/me", { target_job_title });
    setUser(data);
    toast.success(target_job_title ? "Target role updated" : "Target role removed");
  };

  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : "—";

  return (
    <PageContainer width="narrow">
      <PageHeader eyebrow="Account" title="Your profile." />

      <Card padding="p-0" className="overflow-hidden">
        <div className="px-6 py-5 border-b border-border bg-secondary/40 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#0025D8] flex items-center justify-center flex-shrink-0">
            <span className="text-white font-display font-black text-xl">{user.name?.[0]?.toUpperCase() || "?"}</span>
          </div>
          <div>
            <div className="font-display font-bold text-lg text-[#0F172A]">{user.name}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              {user.role} · Member since {memberSince}
            </div>
          </div>
        </div>

        <div className="px-6">
          <InfoRow label="Email" value={user.email} mono />
          <EditableRow label="Full name" value={user.name} placeholder="Your full name" onSave={handleSaveName} />
          <EditableRow label="Target role" value={user.target_job_title} placeholder="e.g. QA Engineer, Software Developer…" onSave={handleSaveTargetRole} />
          <InfoRow label="Auth method" value={user.auth_provider === "google" ? "Google" : "Email & password"} />
        </div>
      </Card>

      <Link to="/subscription" className="block mt-4">
        <Card padding="p-0" hover className="overflow-hidden">
          <div className="px-6 py-4 flex items-center justify-between gap-4 group">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#7C3AED]/10 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-4 h-4 text-[#7C3AED]" />
              </div>
              <div>
                <div className="text-sm font-semibold text-[#0F172A] group-hover:text-[#7C3AED] transition-colors">Subscription & billing</div>
                <div className="font-mono text-[10px] text-muted-foreground">Plan, payment method & invoices</div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-[#7C3AED] transition-colors" />
          </div>
        </Card>
      </Link>

      <Link to="/apply/profile" className="block mt-4">
        <Card padding="p-0" hover className="overflow-hidden">
          <div className="px-6 py-4 flex items-center justify-between gap-4 group">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#EA580C]/10 flex items-center justify-center flex-shrink-0">
                <Rocket className="w-4 h-4 text-[#EA580C]" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#0F172A] group-hover:text-[#EA580C] transition-colors">Apply profile</span>
                  <span className="font-mono text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#EA580C]/10 text-[#EA580C]">Soon</span>
                </div>
                <div className="font-mono text-[10px] text-muted-foreground">Your details for one-click job apply</div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-[#EA580C] transition-colors" />
          </div>
        </Card>
      </Link>
    </PageContainer>
  );
}
